import {
  Component,
  OnInit,
  Input,
  ViewChild,
  ElementRef,
  HostListener,
  Inject,
  PLATFORM_ID,
  Renderer2
} from '@angular/core';
import { NgbModalConfig, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { CommonModule, isPlatformBrowser } from '@angular/common';

import { LoanPurposeType, PageType } from '../../Models/EnumHelper';
import { LoanRequestModel } from '../../Models/loan-request.model';
import { CreditGeniusService } from '../../../Services/credit-genius.service';
import { GetLoanYears } from '../../CommonUtility/util';
import { environment } from '../../../environments/environment';

@Component({
  selector: 'app-navbar',
  standalone: true,
  imports: [CommonModule, RouterModule],
  templateUrl: './navbar.component.html',
  styleUrls: ['./navbar.component.css']
})
export class NavbarComponent implements OnInit {
  @Input() PageType!: PageType;

  @ViewChild('navbar', { static: true })
  navbarRef!: ElementRef<HTMLElement>;
  PgType: typeof PageType = PageType;
  /** for template */
  isShow: boolean = false;
  readonly PurposeTypeEnum = LoanPurposeType;
  purposeType = LoanPurposeType.CarLoan;
  imgSrc = environment.imgBasURL;
  isLoading = false;
  loanList: any[] = [];

  /** slider/form state */
  form: FormGroup;
  durationLabel = 'months';
  durationInYears = 0;

  constructor(
    modalConfig: NgbModalConfig,
    private modalService: NgbModal,
    private fb: FormBuilder,
    private cg: CreditGeniusService,
    private router: Router,
    private renderer: Renderer2,
    @Inject(PLATFORM_ID) private platformId: any
  ) {
    // prevent closing by backdrop/click
    modalConfig.backdrop = 'static';
    modalConfig.keyboard = false;

    // build reactive form
    this.form = this.fb.group({
      loanAmount: [500, [Validators.required, Validators.min(500), Validators.max(25000)]],
      loanTerm: [6, Validators.required]
    });

    // restore previous preference (browser only)
    if (isPlatformBrowser(this.platformId)) {
      const raw = localStorage.getItem('loanprefrence');
      if (raw) {
        try {
          const pref = JSON.parse(raw) as {
            LoanPurpose?: number;
            LoanAmount?: number;
            LoanTerm?: number;
          };
          if (pref.LoanPurpose != null) {
            this.purposeType = pref.LoanPurpose;
          }
          this.form.patchValue({
            loanAmount: pref.LoanAmount ?? this.form.value.loanAmount,
            loanTerm: pref.LoanTerm ?? this.form.value.loanTerm
          });
        } catch { }
      }
    }

    this.syncDurationLabels();
  }

  ngOnInit(): void {
    // nothing else needed here
  }

  /** Expose controls for the template */
  get f() {
    return this.form.controls;
  }

  /** Listen for scroll and toggle .sticky */
  @HostListener('window:scroll')
  onWindowScroll(): void {
    if (!isPlatformBrowser(this.platformId)) return;

    const scrollY = window.pageYOffset || 0;
    if (scrollY > 10) {
      this.renderer.addClass(this.navbarRef.nativeElement, 'sticky');
    } else {
      this.renderer.removeClass(this.navbarRef.nativeElement, 'sticky');
    }
  }

  /** Open/close modal */
  open(content: any) {
    this.modalService.open(content);
  }
  closeAll() {
    this.modalService.dismissAll();
  }

  /** When slider or input changes, keep them in sync */
  onAmountInput(e: Event) {
    const val = Number((e.target as HTMLInputElement).value) || 0;
    // round to nearest 250, min 500
    let cleaned = Math.max(500, Math.ceil(val / 250) * 250);
    this.form.get('loanAmount')!.setValue(cleaned);
  }

  onTermChange(e: Event) {
    const val = Number((e.target as HTMLInputElement).value) || 0;
    this.form.get('loanTerm')!.setValue(val);
    this.syncDurationLabels();
  }

  syncDurationLabels() {
    const term = this.form.value.loanTerm as number;
    if (term >= 12) {
      this.durationLabel = term === 12 ? 'year' : 'years';
      this.durationInYears = GetLoanYears(term);
    } else {
      this.durationLabel = 'months';
      this.durationInYears = term;
    }
  }

  /** Fire the loan eligibility API */
  getEligibilityBasedLoan(origin: 0 | 1) {
    this.isLoading = origin === 1;
    const payload: LoanRequestModel = {
      LoanPurpose: this.purposeType,
      LoanAmount: Number(this.f['loanAmount'].value),
      LoanDuration: Number(this.f['loanTerm'].value),
      CustomerId: isPlatformBrowser(this.platformId)
        ? sessionStorage.getItem('customer') || ''
        : '',
      BrowserName: navigator?.userAgent || ''
    };

    this.cg.GetEligibilityBasedLoan(payload).subscribe({
      next: res => {
        this.isLoading = false;
        if (res.data && res.data.length) {
          this.loanList = res.data;
          this.closeAll();
        } else {
          this.closeAll();
          this.router.navigate(['/primeInfo']);
        }
      },
      error: () => {
        this.isLoading = false;
      }
    });
  }
}
